import { type VFC } from 'react'
import { StaticRouter } from 'react-router-dom/server'
import { useReadMomentAccess } from '@opallabs/api'
import type { User, SimpleWorkspace } from '@opallabs/concepts'
import { type Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { ThemeProvider, WorkspaceContext } from '@opallabs/hooks'
import {
  PermissionsSummary,
  PermissionsSummaryProps,
} from '@opallabs/permissions'

type MomentPermissionSummaryProps = Pick<
  PermissionsSummaryProps,
  'effectivePrivacy' | 'legacyTheme' | 'onOpenShareModal' | 'tooltipZindex'
> &
  Readonly<{
    className?: string
    momentUuid: string
    owner?: User
  }>

const MomentPermissionSummary: VFC<MomentPermissionSummaryProps> = ({
  className,
  effectivePrivacy,
  legacyTheme,
  momentUuid,
  onOpenShareModal,
  owner,
  tooltipZindex,
}) => {
  const accessMetadata = useReadMomentAccess({
    momentUuid,
    enabled: !!momentUuid,
  })

  if (!momentUuid) return null

  return (
    <PermissionsSummary
      className={className}
      effectivePrivacy={effectivePrivacy}
      legacyTheme={legacyTheme}
      metadataQuery={accessMetadata}
      onOpenShareModal={onOpenShareModal}
      resourceName="moment"
      resourceOwner={owner}
      tooltipZindex={tooltipZindex}
    />
  )
}

export const QueryableMomentPermissionSummary: VFC<
  MomentPermissionSummaryProps & { workspace: SimpleWorkspace; theme?: Theme }
> = ({ workspace, theme = 'dark', ...props }) => {
  const location = window.top?.location ?? window.location
  return (
    <DefaultQueryClientProvider>
      <StaticRouter location={location.pathname}>
        <WorkspaceContext.Provider value={workspace}>
          <ThemeProvider theme={theme}>
            <MomentPermissionSummary {...props} />
          </ThemeProvider>
        </WorkspaceContext.Provider>
      </StaticRouter>
    </DefaultQueryClientProvider>
  )
}
